import { useEffect, useRef, useState } from "react";
import {
  ArrowCounterClockwise,
  Check,
  CheckCircle,
  Checks,
  PencilSimple,
  Prohibit,
  Sparkle,
  ThumbsUp,
  X,
} from "@phosphor-icons/react";
import type { AudienceCategory, RoomThread } from "../../shared/types.ts";
import { authorDisplay, fmtAgo } from "../lib/threads.ts";
import { OverflowMenu, type MenuItem } from "./OverflowMenu.tsx";
import { ProjectToggle } from "./ProjectToggle.tsx";
import { ThreadReplies } from "./ThreadReplies.tsx";

const EMPTY_PENDING_REPLY_IDS: ReadonlySet<string> = new Set();

/** The category text in the row meta doubles as a quiet popover trigger for
 *  re-filing a thread or re-running AI categorization. No extra controls at
 *  rest — manual filings hide the AI option (they are never overwritten). */
function CategoryMenu({ thread, categories, disabled, onSelect, onRerun }: {
  thread: RoomThread;
  categories: AudienceCategory[];
  disabled: boolean;
  onSelect: (categoryId: string) => void;
  onRerun: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);
  const label = categories.find((category) => category.id === thread.categoryId)?.label
    ?? (thread.aiCategoryStatus === "pending" ? "categorizing…" : "uncategorized");
  const canRerun = thread.categorySource !== "manual";
  return (
    <span className="menu-pop" ref={ref}>
      <button
        type="button"
        className="expand"
        data-testid="thread-category-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change category"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>
      {open && (
        <div className="pop" role="menu">
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              role="menuitem"
              data-testid={`thread-category-option-${category.slug}`}
              onClick={() => {
                setOpen(false);
                if (category.id !== thread.categoryId) onSelect(category.id);
              }}
            >
              {category.id === thread.categoryId ? <Check weight="bold" /> : null} {category.label}
            </button>
          ))}
          {canRerun && (
            <button
              type="button"
              role="menuitem"
              data-testid="thread-category-rerun"
              onClick={() => {
                setOpen(false);
                onRerun();
              }}
            >
              <Sparkle weight="fill" /> Re-run AI
            </button>
          )}
        </div>
      )}
    </span>
  );
}

export interface ThreadActions {
  approve: (t: RoomThread) => void;
  answer: (t: RoomThread) => void;
  reopen: (t: RoomThread) => void;
  hide: (t: RoomThread) => void;
  saveEdit: (t: RoomThread, body: string) => void;
  approveReply: (replyId: string) => void;
  hideReply: (replyId: string) => void;
  project: (t: RoomThread) => void;
  stopProjection: () => void;
  setCategory: (t: RoomThread, categoryId: string) => void;
  recategorize: (t: RoomThread) => void;
}

/** Compact table-like thread row for the Admin threads panel. */
export function ThreadRow({
  thread,
  categories,
  onAir,
  actions,
  pending = false,
  pendingReplyIds = EMPTY_PENDING_REPLY_IDS,
  projectorPending = false,
}: {
  thread: RoomThread;
  categories: AudienceCategory[];
  onAir: boolean;
  actions: ThreadActions;
  pending?: boolean;
  pendingReplyIds?: ReadonlySet<string>;
  projectorPending?: boolean;
}) {
  // Replies render inline by default (consistent with moderator/participant);
  // the meta toggle collapses them for dense triage.
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(thread.body);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const author = authorDisplay(thread);
  const isAi = thread.threadOrigin === "ai";
  const canProject = thread.status === "shown" || thread.status === "answered";
  const canEdit = Boolean(thread.editableByStaff);
  const replies = thread.replies ?? [];
  const visibleReplyCount = replies.filter((r) => r.status === "shown" || r.status === "queued").length;

  function startEdit() {
    if (!canEdit || pending) return;
    setDraft(thread.body);
    setEditing(true);
    requestAnimationFrame(() => editorRef.current?.select());
  }

  function cancelEdit() {
    setDraft(thread.body);
    setEditing(false);
  }

  function saveEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === thread.body) {
      cancelEdit();
      return;
    }
    actions.saveEdit(thread, trimmed);
    setEditing(false);
  }

  const overflow: MenuItem[] = [
    ...(thread.status === "answered"
      ? [{ label: <><ArrowCounterClockwise weight="bold" /> Reopen</>, onClick: () => actions.reopen(thread), disabled: pending }]
      : []),
    ...(thread.status === "shown" || thread.status === "answered"
      ? [{ label: <><Prohibit weight="bold" /> Hide</>, onClick: () => actions.hide(thread), danger: true, testid: "thread-hide-item", disabled: pending }]
      : []),
  ];

  return (
    <div
      className={`thread-row status-${thread.status} ${isAi ? "is-ai" : ""} ${onAir ? "is-onair" : ""} ${pending ? "is-pending" : ""} ${(expanded && visibleReplyCount > 0) || editing ? "expanded" : ""}`}
      data-testid="room-thread-card"
      data-thread-kind={thread.threadKind}
      data-status={thread.status}
      aria-busy={pending}
    >
      <div className="edge" />
      <div className="rt-main">
        <div className="rt-meta">
          <span className={`who ${author.kind === "ai" ? "ai" : ""} ${author.kind === "anonymous" ? "anon" : ""}`}>
            {author.kind === "ai" && <Sparkle weight="fill" />} {author.label}
          </span>
          <span className="sep">·</span>
          <CategoryMenu
            thread={thread}
            categories={categories}
            disabled={pending}
            onSelect={(categoryId) => actions.setCategory(thread, categoryId)}
            onRerun={() => actions.recategorize(thread)}
          />
          {thread.likeCount > 0 && <><span className="sep">·</span><ThumbsUp weight="bold" /> {thread.likeCount}</>}
          <span className="sep">·</span>{fmtAgo(thread.createdAt)}
          {visibleReplyCount > 0 && (
            <>
              <span className="sep">·</span>
              <button type="button" className="expand" data-testid="thread-replies-toggle" onClick={() => setExpanded((v) => !v)}>
                {expanded ? "▾" : "▸"} {visibleReplyCount} {visibleReplyCount === 1 ? "reply" : "replies"}
              </button>
            </>
          )}
          {thread.status === "answered" && (
            <><span className="sep">·</span><span className="badge status status-answered">answered</span></>
          )}
          {thread.staleSourceAt && (
            <><span className="sep">·</span><span className="badge status status-stale-source" data-testid="thread-stale-source" title="The source transcript was corrected after this question was generated.">source corrected</span></>
          )}
        </div>
        {editing ? (
          <div className="room-thread-editor">
            <textarea
              ref={editorRef}
              value={draft}
              rows={3}
              data-testid="thread-edit-textarea"
              disabled={pending}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  saveEdit();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
            />
            <div className="row" style={{ marginBottom: 0 }}>
              <button type="button" className="btn primary sm" data-testid="thread-edit-save" disabled={pending} onClick={saveEdit}>
                <CheckCircle weight="bold" /> Save
              </button>
              <button type="button" className="btn ghost sm" disabled={pending} onClick={cancelEdit}>
                <X weight="bold" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`rt-body room-thread-body ${canEdit ? "is-editable" : ""}`}
            data-testid={canEdit ? "thread-body-edit-trigger" : undefined}
            role={canEdit ? "button" : undefined}
            tabIndex={canEdit ? 0 : undefined}
            title={canEdit ? "Click to edit" : undefined}
            onClick={startEdit}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && canEdit) {
                e.preventDefault();
                startEdit();
              }
            }}
          >
            <span>{thread.body}</span>
            {canEdit && <PencilSimple weight="bold" className="thread-inline-edit-hint" />}
          </div>
        )}
      </div>
      <div className="rt-acts">
        {thread.status === "queued" && (
          <div className="thread-queue-actions">
            <button
              type="button"
              className="btn icon thread-action-circle thread-action-approve"
              title="Approve"
              aria-label="Approve thread"
              data-testid="thread-approve-btn"
              disabled={pending}
              onClick={() => actions.approve(thread)}
            >
              <Check weight="bold" />
            </button>
            <button
              type="button"
              className="btn icon thread-action-circle thread-action-reject"
              title="Reject / hide"
              aria-label="Reject thread"
              data-testid="thread-reject-btn"
              disabled={pending}
              onClick={() => actions.hide(thread)}
            >
              <X weight="bold" />
            </button>
          </div>
        )}
        {thread.status === "hidden" && (
          <button
            type="button"
            className="btn icon thread-action-circle thread-action-approve"
            title="Approve"
            aria-label="Approve thread"
            data-testid="thread-approve-btn"
            disabled={pending}
            onClick={() => actions.approve(thread)}
          >
            <Check weight="bold" />
          </button>
        )}
        {thread.status === "shown" && (
          <>
            <ProjectToggle onAir={onAir} canProject={canProject}
              onProject={() => actions.project(thread)} onStop={actions.stopProjection}
              variant="icon" disabled={pending || projectorPending} />
            <button type="button" className="btn icon thread-action-answer" title="Mark answered"
              aria-label="Mark answered" data-testid="thread-mark-answered-btn" disabled={pending}
              onClick={() => actions.answer(thread)}>
              <Checks weight="bold" />
            </button>
          </>
        )}
        {thread.status === "answered" && (
          <button type="button" className="btn ghost sm" disabled={pending} onClick={() => actions.reopen(thread)}>
            <ArrowCounterClockwise weight="bold" /> Reopen
          </button>
        )}
        <OverflowMenu items={overflow} testid="thread-hide-btn" disabled={pending} />
      </div>
      {expanded && (
        <ThreadReplies
          replies={replies}
          onApproveReply={actions.approveReply}
          onHideReply={actions.hideReply}
          pendingReplyIds={pendingReplyIds}
        />
      )}
    </div>
  );
}
